import { useCallback, useEffect, useRef, useState } from "react";
import {
  agregarMateriales,
  leerMaterialesGuardados,
  quitarMaterial,
  type MaterialConocimiento,
  type ResumenAgregado,
} from "./materialesConocimiento";
import type { MotivoSinPersistir } from "./useCanalesReferencia";

/**
 * El material de la base de conocimiento (archivos anotados, páginas y textos),
 * guardado POR MARCA en localStorage.
 *
 * Gemelo de `useCanalesReferencia`: misma clave con el id de la marca adentro,
 * misma lectura blindada, misma escritura tolerante al storage deshabilitado y
 * la misma fusión de lo cargado sin marca con lo que la marca ya tenía (nunca
 * pisar: es una lista acumulativa, no un objeto de config). Se repite la forma
 * a propósito en vez de generalizar las dos en un hook con genéricos: lo único
 * que compartirían es el andamiaje, y las dos listas se van a mudar al backend
 * por caminos distintos (una es una tabla de fuentes, la otra necesita
 * almacenamiento de archivos que hoy no existe).
 *
 * Y lo que NO hace, que es lo importante: NO SUBE NADA. De un archivo elegido
 * acá entran su nombre, su tamaño y su tipo, porque en localStorage no entra un
 * archivo y no hay backend donde ponerlo. Ver el encabezado de
 * `materialesConocimiento.ts` y ADR-2026-08-17-estudio-marketing-modal-anadir-fuente.
 */

const PREFIJO = "atlas:estudio-marketing:materiales-conocimiento:";

export const claveMateriales = (brandId: string) => `${PREFIJO}${brandId}`;

/** El `try` cubre el storage deshabilitado (Safari privado, iframes con cookies
 *  bloqueadas), donde `getItem` no devuelve null sino que TIRA. */
function leerDelStorage(brandId: string | null): MaterialConocimiento[] {
  if (!brandId) return [];
  try {
    return leerMaterialesGuardados(window.localStorage.getItem(claveMateriales(brandId)));
  } catch {
    return [];
  }
}

/** Devuelve si pudo escribir. Acá el `false` es MÁS probable que en los
 *  canales: un texto pegado largo puede pasarse de la cuota. La pantalla usa
 *  ese `false` para avisar que lo cargado no sobrevive al reload. */
function escribirEnStorage(brandId: string, materiales: MaterialConocimiento[]): boolean {
  try {
    window.localStorage.setItem(claveMateriales(brandId), JSON.stringify(materiales));
    return true;
  } catch {
    return false;
  }
}

export interface MaterialesConocimiento {
  materiales: MaterialConocimiento[];
  /** Agrega una tanda (los archivos entran de a varios) y devuelve el resumen:
   *  cuántos entraron y cuántos ya estaban. */
  agregar: (nuevos: MaterialConocimiento[]) => ResumenAgregado;
  quitar: (id: string) => void;
  persiste: boolean;
  motivoSinPersistir: MotivoSinPersistir | null;
}

export function useMaterialesConocimiento(brandId: string | null): MaterialesConocimiento {
  const [materiales, setMateriales] = useState<MaterialConocimiento[]>(() =>
    leerDelStorage(brandId),
  );
  const [storageOk, setStorageOk] = useState(true);
  /** Lo que se cargó mientras `brandId` todavía era `null`. */
  const pendienteSinMarca = useRef<MaterialConocimiento[] | null>(null);

  useEffect(() => {
    const pendiente = pendienteSinMarca.current;
    if (brandId && pendiente) {
      // Llegó la marca: se FUSIONA con lo que ya tenía guardada, no se pisa.
      // Lo pendiente es un delta armado sobre una lista vacía (sin marca no hay
      // clave que leer), así que escribirlo solo borraría todo lo anterior.
      pendienteSinMarca.current = null;
      const fusionados = agregarMateriales(leerDelStorage(brandId), pendiente).materiales;
      setMateriales(fusionados);
      setStorageOk(escribirEnStorage(brandId, fusionados));
      return;
    }
    // Cambió la marca activa ⇒ la base de conocimiento es otra.
    setMateriales(leerDelStorage(brandId));
    setStorageOk(true);
  }, [brandId]);

  const agregar = useCallback(
    (nuevos: MaterialConocimiento[]): ResumenAgregado => {
      const resumen = agregarMateriales(materiales, nuevos);
      if (resumen.agregados === 0) return resumen;
      setMateriales(resumen.materiales);
      if (!brandId) {
        pendienteSinMarca.current = resumen.materiales;
        return resumen;
      }
      setStorageOk(escribirEnStorage(brandId, resumen.materiales));
      return resumen;
    },
    [materiales, brandId],
  );

  const quitar = useCallback(
    (id: string) => {
      const siguiente = quitarMaterial(materiales, id);
      setMateriales(siguiente);
      if (!brandId) {
        pendienteSinMarca.current = siguiente;
        return;
      }
      setStorageOk(escribirEnStorage(brandId, siguiente));
    },
    [materiales, brandId],
  );

  return {
    materiales,
    agregar,
    quitar,
    persiste: Boolean(brandId) && storageOk,
    motivoSinPersistir: !brandId ? "sin-marca" : storageOk ? null : "storage",
  };
}
